import { Button, Label, ToggleSwitch } from 'flowbite-react';
import React from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { MultiValue } from 'react-select';
import Select from 'react-select';
import { Dropdown, locationPreferenceOptions } from 'shared-utils';
import { useToast } from 'src/context/ToastProvider';
import { FilterCompaniesInput } from 'types';
import { useRecommendationsContext } from 'src/context/RecommendationsProvider';
import { getMyCompaniesQuery } from 'src/apis/company';
import { useGetMyBDPocsQuery } from 'src/hooks/user.hooks';
import { filter, get } from 'lodash';
import { useSearchParams } from 'react-router-dom';

const CompaniesFiltersForm = ({
	onClose,
	onApply,
	onClear,
}: {
	companyId?: string;
	onClose?: () => void;
	onApply?: () => void;
	onClear?: () => void;
	getFilterData?: (filters: FilterCompaniesInput) => any;
}) => {
	const toast = useToast();
	const queryClient = useQueryClient();
	const [searchParams, setSearchParams] = useSearchParams();
	const [filters, setFilters] = React.useState<FilterCompaniesInput | null>();
	const [value, setValue] = React.useState<MultiValue<Dropdown>>([]);
	const [skills, setSkills] = React.useState<MultiValue<Dropdown>>([]);
	const {
		setIsFilterLoading,
		setTotalCompaniesCount,
		setTotalSelfCompaniesCount,
		setTotalActiveCompaniesCount,
		setCompanies,
		setActiveCompanies,
		setSelfCompanies,
		setCompanyLocationFilterData,
	} = useRecommendationsContext();

	const { data: myBDPOCsData, isLoading: bdsLoading } = useGetMyBDPocsQuery();

	const filterCompaniesFn = useMutation(['getMyCompaniesQuery'], getMyCompaniesQuery, {
		onSuccess(data) {
			setIsFilterLoading(false);
			setCompanies(data?.companies || []);
			setTotalCompaniesCount(data?.total || 0);
			setTotalSelfCompaniesCount(data?.self || 0);
			setTotalActiveCompaniesCount(data?.active || 0);
			setActiveCompanies(data?.activeCompanies || []);
			setSelfCompanies(data?.selfCompanies || []);
			queryClient.setQueryData(['getMyCompaniesQuery'], data);
			onClose && onClose();
		},
		onError(error: any) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			setIsFilterLoading(false);
			toast({
				title: errorMessage,
				type: 'error',
				duration: 5000,
			});
		},
	});

	const locationOptions = locationPreferenceOptions.map(l => ({ label: l, value: l }));
	const bdOptions = myBDPOCsData?.bds?.map(bd => ({ label: `${bd.name} - ${bd.email}`, value: bd._id }));

	return (
		<form
			onSubmit={e => {
				e.preventDefault();
				setIsFilterLoading(true);
				setCompanyLocationFilterData(value.map(data => data.value));
				console.log('filters:', filters);
				if (filters) {
					Object.keys(filters).map((filter: string) => {
						// @ts-ignore
						searchParams.set(filter, filters[filter]);
					});
					setSearchParams(searchParams);
				}

				filterCompaniesFn.mutate({ ...filters, limit: 12, locations: JSON.stringify(value.map(v => v.value)) });
			}}
		>
			<div className="mt-[20px] whiteBgSelect ">
				<div className="mb-4 block">
					<Label htmlFor="location" className="font-[700] font-inter text-sm text-[#28293D]" value="Location" />
				</div>

				<Select
					isClearable
					isMulti
					options={locationOptions}
					onChange={(vals: MultiValue<Dropdown>) => {
						setValue(vals);
						setFilters(prev => {
							return { ...prev };
						});
					}}
					menuPlacement="auto"
					className="[&>*]:!min-h-[46px]"
					classNamePrefix="my-select-plans"
					placeholder="Mumbai, Bangalore"
					blurInputOnSelect={false}
					closeMenuOnSelect={false}
					value={value}
				/>
			</div>

			<div className="mt-[20px] whiteBgSelect">
				<div className="mb-4 block">
					<Label htmlFor="bdpocs" className="font-[700] font-inter text-sm text-[#28293D]" value="BD" />
				</div>

				<Select
					isClearable
					isMulti
					isLoading={bdsLoading}
					options={bdOptions}
					onChange={(vals: MultiValue<Dropdown>) => {
						setFilters(prev => ({
							...prev,
							bdpocs: JSON.stringify(vals.map(val => val.value)),
						}));
					}}
					menuPlacement="auto"
					className="[&>*]:!min-h-[46px]"
					classNamePrefix="my-select-plans"
					placeholder="abc@gmail.com"
					blurInputOnSelect={false}
					closeMenuOnSelect={false}
				/>
			</div>

			<div className="mt-[20px] flex flex-col gap-2 whiteBgSelect">
				<ToggleSwitch
					label="Pending Action"
					checked={filters?.isPendingAction || false}
					onChange={e => setFilters(prev => ({ ...prev, isPendingAction: e }))}
				/>
				<ToggleSwitch
					label="Qualified Leads"
					checked={filters?.isQualified || false}
					onChange={e => setFilters(prev => ({ ...prev, isQualified: e }))}
				/>
				<ToggleSwitch
					label="Disqualified Leads"
					checked={filters?.isDisqualified || false}
					onChange={e => setFilters(prev => ({ ...prev, isDisqualified: e }))}
				/>
				<ToggleSwitch
					label="Self Created Positions"
					checked={filters?.selfCreatedPosition || false}
					onChange={e => setFilters(prev => ({ ...prev, selfCreatedPosition: e }))}
				/>
			</div>

			<div className="flex flex-wrap gap-[20px] mt-[17px]">
				<Button
					type="reset"
					color="light"
					disabled={filterCompaniesFn.isLoading}
					className="flex-1"
					onClick={() => {
						setValue([]);
						setSkills([]);
						setFilters(null);
						setIsFilterLoading(true);
						filterCompaniesFn.mutate({ limit: 12 });
						onClear && onClear();
						if (filters) {
							Object.keys(filters).map(filter => {
								searchParams.delete(filter);
							});
							setSearchParams(searchParams);
						}
					}}
				>
					Clear
				</Button>
				<Button
					disabled={filterCompaniesFn.isLoading}
					isProcessing={filterCompaniesFn.isLoading}
					type="submit"
					color="purplePlaced"
					className="flex-1 bg-[#7777E7]"
					onClick={onApply}
				>
					Apply
				</Button>
			</div>
		</form>
	);
};

export default CompaniesFiltersForm;
